import useSWR, { mutate } from "swr";
import { useContext, useMemo } from "react";

import { DemoContext } from "contexts/demo";
import { singleFetch } from "lib/fetch";

export const inmutableConfig = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 60 * 60 * 1000, // 60 minutes
};

export const liveConfig = {
  refreshInterval: 3000,
};

type PathType = string | false | undefined | null;

export const useAPI = <T>(path: PathType, isLive?: boolean) => {
  const { isDemo } = useContext(DemoContext);
  const config = useMemo(() => {
    if (isLive) return liveConfig;
    return inmutableConfig;
  }, [isLive]);

  const {
    data,
    error,
    isLoading,
    mutate: boundMutate,
  } = useSWR<T>(path, (url) => singleFetch(url, isDemo), config);

  const mutateAndKeepDataOnFailure = async () => {
    if (typeof path !== "string" || !path) {
      // console.error("Invalid or missing path for data fetch.");
      return;
    }

    try {
      const newData: T | any = await singleFetch(path, isDemo);
      if (newData) {
        if (newData?.data?.length > 0 || !newData.data) {
          await boundMutate(newData, false);
        }
      }
    } catch (error) {
      console.error(
        "Error fetching new data. Maintaining existing data.",
        error
      );
    }
  };

  const isPaused = useMemo(
    () => !data && !isLoading && !error,
    [data, isLoading, error]
  );

  return {
    data,
    isLoading,
    error,
    isPaused,
    mutate: mutateAndKeepDataOnFailure,
  };
};
